import { Component, Input, Output, EventEmitter, OnInit, OnChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { GenericCommanService } from '../../../services/generic-common.service';
import { GenericPromptBuilder, GenericPromptParams } from '../../utils/generic-prompt-builder';

@Component({
  selector: 'app-generic-content-generation-popup',
  standalone: true,
  imports: [CommonModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatProgressSpinnerModule],
  templateUrl: './generic-content-generation-popup.component.html',
  styleUrl: './generic-content-generation-popup.component.scss'
})
export class GenericContentGenerationPopupComponent implements OnInit { // OnChanges
  @Input() recommendation: any;
  @Input() isVisible: boolean = false;
  @Output() close = new EventEmitter<void>();

  isLoading: boolean = false;
  detailedContent: any = null;
  error: string | null = null;

  // getLoading(): boolean {
  //   return this.isLoading;
  // }

  constructor(private genericService: GenericCommanService, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void { }

  // Reset all data when popup opens
  private resetData(): void {
    // console.log('🔄 Resetting popup data...');
    this.isLoading = false;
    this.error = null;
    this.detailedContent = null;

    // Force change detection after reset
    this.cdr.detectChanges();

    // console.log('✅ Popup data reset complete');
  }

  // Public method to manually reset data
  public resetPopupData(): void {
    // console.log('🧹 Manual reset requested...');
    this.resetData();
  }

  // Watch for isVisible changes and reset data when popup opens
  ngOnChanges(changes: any): void {
    if (changes['isVisible'] && changes['isVisible'].currentValue === true) {
      // console.log('🎯 Popup opened, resetting data...');
      this.resetData();
    }
  }

  generateContent(): void {
    if (!this.recommendation) return;

    // console.log('🚀 generateContent called with recommendation:', this.recommendation);

    // Clear previous content and set loading state
    this.isLoading = true;
    this.cdr.markForCheck();
    this.error = null;
    this.detailedContent = null;

    // Force change detection for loading state
    this.cdr.detectChanges();

    // console.log('🔄 Loading state set to:', this.isLoading);

    // Get brand name from recommendation
    const brandName = this.recommendation.brand || 'Brand';

    // Build structured prompt for content generation using simple-search API
    const promptParams: GenericPromptParams = {
      brands: [brandName],
      industry: this.recommendation.category || 'General',
      country: 'Global',
      category: this.recommendation.category,
      companySize: 'any',
      marketCap: 'any',
      businessModel: 'any',
      specialization: 'any',
      useCase: 'content_generation'
    };

    // Create content generation specific prompt
    const contentPrompt = this.buildContentGenerationPrompt(brandName, this.recommendation);

    // Prepare payload for simple-search API
    const payload = {
      prompt: contentPrompt,
      web_search: true,
      brand_analysis: true,
      structured_response: true,
      your_brand: brandName,
      competitor_brands: [],
      reload: true
    };

    console.log('📤 Content Generation API Payload:', payload);

    this.genericService.simpleSearch(payload).subscribe({
      next: (response) => {
        console.log('📥 Content Generation API Response:', response);

        // Parse the structured response from simple-search API
        let structuredData = null;
        
        if (response.structured_data) {
          structuredData = response.structured_data;
        } else if (response.response) {
          try {
            // The response might contain JSON as a string, try to extract it
            let responseText = response.response;
            
            // Look for JSON content within the response string
            const jsonMatch = responseText.match(/```json\s*([\s\S]*?)\s*```/);
            if (jsonMatch && jsonMatch[1]) {
              // Extract JSON from code block
              structuredData = JSON.parse(jsonMatch[1].trim());
            } else {
              // Try to parse the entire response as JSON
              structuredData = JSON.parse(responseText);
            }
          } catch (e) {
            console.warn('Failed to parse structured response, trying alternative parsing:', e);
            
            // Try to extract JSON from the response text using regex
            try {
              const jsonStart = response.response.indexOf('{');
              const jsonEnd = response.response.lastIndexOf('}') + 1;
              
              if (jsonStart !== -1 && jsonEnd > jsonStart) {
                const jsonString = response.response.substring(jsonStart, jsonEnd);
                structuredData = JSON.parse(jsonString);
              } else {
                console.error('No valid JSON found in response');
                structuredData = { raw_response: response.response };
              }
            } catch (parseError) {
              console.error('Failed to parse JSON from response:', parseError);
              structuredData = { raw_response: response.response };
            }
          }
        }

        console.log('📊 Parsed structured data:', structuredData);

        if (structuredData && structuredData.detailed_content) {
          // Process the detailed content to make it brand-appropriate
          this.detailedContent = this.processDetailedContent(structuredData.detailed_content, brandName);
          
          this.cdr.markForCheck();
          console.log('✅ Content generated successfully for:', brandName);
          console.log('✅ Detailed content:', this.detailedContent);
        } else if (structuredData && (structuredData.composite_score || structuredData.competitor_analysis)) {
          // Handle brand analysis response - convert to content generation format
          console.log('🔄 Converting brand analysis response to content generation format');
          this.detailedContent = this.convertBrandAnalysisToContent(structuredData, brandName, this.recommendation);
          
          this.cdr.markForCheck();
          console.log('✅ Content generated from brand analysis for:', brandName);
          console.log('✅ Detailed content:', this.detailedContent);
        } else {
          console.error('❌ API response invalid or missing expected content:', response);
          console.error('❌ Parsed structured data:', structuredData);
          this.error = 'Failed to generate content. Please try again.';
        }

        this.isLoading = false;
        this.cdr.markForCheck();
        console.log('🏁 Loading finished, isLoading:', this.isLoading);
        console.log('🏁 Final detailedContent:', this.detailedContent);

        // Force change detection after all updates
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ Error generating content:', error);
        this.error = 'An error occurred while generating content. Please try again.';
        this.isLoading = false;
        this.cdr.markForCheck();

        // Force change detection after error
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Process detailed content to make it brand-appropriate (replace university terms with brand terms)
   */
  private processDetailedContent(content: any, brandName: string): any {
    if (!content) {
      return content;
    }

    // Deep clone the content to avoid mutating the original
    const processedContent = JSON.parse(JSON.stringify(content));

    // Helper function to replace university-specific terms with brand-specific terms
    const replaceUniversityTerms = (text: string): string => {
      if (!text || typeof text !== 'string') return text;

      let result = text;

      // Step 1: Replace [University Name] placeholder with actual brand name
      result = result.replace(/\[University Name\]/gi, brandName);
      result = result.replace(/\[Brand Name\]/gi, brandName);

      // Step 2: Handle specific university name patterns (e.g., "Harvard University" → "Harvard")
      // This regex finds patterns like "Brand University" and removes "University"
      result = result.replace(new RegExp(`\\b${brandName}\\s+University\\b`, 'gi'), brandName);
      result = result.replace(new RegExp(`\\b${brandName}\\s+College\\b`, 'gi'), brandName);
      
      // Step 3: Replace compound terms first (more specific to less specific)
      result = result
        // Prospective students
        .replace(/\bprospective undergraduate student(s)?\b/gi, (match) => match.includes('s') ? 'potential customers' : 'potential customer')
        .replace(/\bprospective student(s)?\b/gi, (match) => match.includes('s') ? 'potential customers' : 'potential customer')
        .replace(/\bProspective undergraduate student(s)?\b/gi, (match) => match.includes('s') ? 'Potential customers' : 'Potential customer')
        .replace(/\bProspective student(s)?\b/gi, (match) => match.includes('s') ? 'Potential customers' : 'Potential customer')
        
        // Undergraduate/graduate students
        .replace(/\bundergraduate student(s)?\b/gi, (match) => match.includes('s') ? 'customers' : 'customer')
        .replace(/\bUndergraduate student(s)?\b/gi, (match) => match.includes('s') ? 'Customers' : 'Customer')
        
        // Students
        .replace(/\bstudent(s)?\b/gi, (match) => match.toLowerCase().endsWith('s') ? 'customers' : 'customer')
        .replace(/\bStudent(s)?\b/gi, (match) => match.endsWith('s') ? 'Customers' : 'Customer')
        
        // Programs and courses
        .replace(/\bprogram overview\b/gi, 'product overview')
        .replace(/\bProgram overview\b/gi, 'Product overview')
        .replace(/\bprogram(s)?\b/gi, (match) => match.toLowerCase().endsWith('s') ? 'products' : 'product')
        .replace(/\bProgram(s)?\b/gi, (match) => match.endsWith('s') ? 'Products' : 'Product')
        .replace(/\bcourse(s)?\b/gi, (match) => match.toLowerCase().endsWith('s') ? 'services' : 'service')
        .replace(/\bCourse(s)?\b/gi, (match) => match.endsWith('s') ? 'Services' : 'Service')
        
        // Admission and enrollment
        .replace(/\badmission process\b/gi, 'customer journey')
        .replace(/\bAdmission process\b/gi, 'Customer journey')
        .replace(/\badmission page\b/gi, 'get started page')
        .replace(/\bAdmission page\b/gi, 'Get started page')
        .replace(/\badmission(s)?\b/gi, 'engagement')
        .replace(/\bAdmission(s)?\b/gi, 'Engagement')
        .replace(/\benrollment\b/gi, 'conversion')
        .replace(/\bEnrollment\b/gi, 'Conversion')
        
        // Faculty and professors
        .replace(/\bfaculty spotlight\b/gi, 'team spotlight')
        .replace(/\bFaculty spotlight\b/gi, 'Team spotlight')
        .replace(/\bfaculty expertise\b/gi, 'team expertise')
        .replace(/\bFaculty expertise\b/gi, 'Team expertise')
        .replace(/\bfaculty member(s)?\b/gi, (match) => match.includes('s') ? 'team members' : 'team member')
        .replace(/\bFaculty member(s)?\b/gi, (match) => match.includes('s') ? 'Team members' : 'Team member')
        .replace(/\bfaculty\b/gi, 'team')
        .replace(/\bFaculty\b/gi, 'Team')
        .replace(/\bprofessor(s)?\b/gi, (match) => match.toLowerCase().endsWith('s') ? 'experts' : 'expert')
        .replace(/\bProfessor(s)?\b/gi, (match) => match.endsWith('s') ? 'Experts' : 'Expert')
        
        // University and college
        .replace(/\buniversity homepage\b/gi, `${brandName} homepage`)
        .replace(/\bUniversity homepage\b/gi, `${brandName} homepage`)
        .replace(/\buniversity website\b/gi, 'website')
        .replace(/\bUniversity website\b/gi, 'Website')
        .replace(/\buniversity('s)?\b/gi, (match) => match.includes("'s") ? `${brandName}'s` : brandName)
        .replace(/\bUniversity('s)?\b/gi, (match) => match.includes("'s") ? `${brandName}'s` : brandName)
        .replace(/\bcollege\b/gi, 'brand')
        .replace(/\bCollege\b/gi, 'Brand')
        
        // Campus and facilities
        .replace(/\bcampus life\b/gi, 'brand experience')
        .replace(/\bCampus life\b/gi, 'Brand experience')
        .replace(/\bcampus\b/gi, 'organization')
        .replace(/\bCampus\b/gi, 'Organization')
        
        // Academic terms
        .replace(/\bacademic year\b/gi, 'fiscal year')
        .replace(/\bAcademic year\b/gi, 'Fiscal year')
        .replace(/\bacademic\b/gi, 'professional')
        .replace(/\bAcademic\b/gi, 'Professional')
        .replace(/\beducation\b/gi, 'experience')
        .replace(/\bEducation\b/gi, 'Experience')
        .replace(/\bcurriculum\b/gi, 'offerings')
        .replace(/\bCurriculum\b/gi, 'Offerings')
        
        // Graduates and alumni
        .replace(/\bgraduate(s)?\b/gi, (match) => match.toLowerCase().endsWith('s') ? 'clients' : 'client')
        .replace(/\bGraduate(s)?\b/gi, (match) => match.endsWith('s') ? 'Clients' : 'Client')
        .replace(/\balumni\b/gi, 'past customers')
        .replace(/\bAlumni\b/gi, 'Past customers');

      // Step 4: Clean up any double spaces or awkward phrasings
      result = result
        .replace(/\s+/g, ' ') // Remove multiple spaces
        .replace(/\bbrand brand\b/gi, brandName) // Fix "brand brand" → brand name
        .replace(/\bBrand brand\b/gi, brandName) // Fix "Brand brand" → brand name
        .replace(/\bBrand Brand\b/gi, brandName); // Fix "Brand Brand" → brand name

      return result.trim();
    };

    // Process overview
    if (processedContent.content_overview) {
      processedContent.content_overview = replaceUniversityTerms(processedContent.content_overview);
    }

    // Process timeline
    if (processedContent.implementation_timeline) {
      processedContent.implementation_timeline = replaceUniversityTerms(processedContent.implementation_timeline);
    }

    // Process content pieces
    if (processedContent.content_pieces && Array.isArray(processedContent.content_pieces)) {
      processedContent.content_pieces = processedContent.content_pieces.map((piece: any) => ({
        ...piece,
        title: replaceUniversityTerms(piece.title),
        content: replaceUniversityTerms(piece.content),
        target_audience: replaceUniversityTerms(piece.target_audience),
        platform: piece.platform, // Platform names stay the same
        call_to_action: replaceUniversityTerms(piece.call_to_action),
        visual_suggestions: replaceUniversityTerms(piece.visual_suggestions),
        seo_keywords: piece.seo_keywords ? piece.seo_keywords.map((kw: string) => replaceUniversityTerms(kw)) : [],
        hashtags: piece.hashtags // Hashtags stay the same
      }));
    }

    // Process success metrics
    if (processedContent.success_metrics && Array.isArray(processedContent.success_metrics)) {
      processedContent.success_metrics = processedContent.success_metrics.map((metric: string) => 
        replaceUniversityTerms(metric)
      );
    }

    // Process next steps
    if (processedContent.next_steps && Array.isArray(processedContent.next_steps)) {
      processedContent.next_steps = processedContent.next_steps.map((step: string) => 
        replaceUniversityTerms(step)
      );
    }

    return processedContent;
  }

  copyContent(content: any): void {
    const textToCopy = this.formatContentForCopy(content);
    navigator.clipboard.writeText(textToCopy).then(() => {
      // console.log('Content copied to clipboard');
    });
  }

  private formatContentForCopy(content: any): string {
    const brandName = this.recommendation?.brand || 'Brand';
    const category = this.recommendation?.category || 'Content';
    
    let formattedText = `Content Generation for ${brandName}\n`;
    formattedText += `Category: ${category}\n`;
    formattedText += `Generated: ${new Date().toLocaleString()}\n`;
    formattedText += `${'='.repeat(60)}\n\n`;

    if (content.content_overview) {
      formattedText += `Content Overview:\n${content.content_overview}\n\n`;
    }

    if (content.implementation_timeline) {
      formattedText += `Implementation Timeline:\n${content.implementation_timeline}\n\n`;
    }

    if (content.content_pieces && content.content_pieces.length > 0) {
      formattedText += 'Content Pieces:\n';
      content.content_pieces.forEach((piece: any, index: number) => {
        formattedText += `\n${index + 1}. ${piece.title}\n`;
        formattedText += `Type: ${piece.type}\n`;
        formattedText += `Content: ${piece.content}\n`;
        if (piece.target_audience) {
          formattedText += `Target Audience: ${piece.target_audience}\n`;
        }
        if (piece.platform) {
          formattedText += `Platform: ${piece.platform}\n`;
        }
        if (piece.call_to_action) {
          formattedText += `Call to Action: ${piece.call_to_action}\n`;
        }
        if (piece.seo_keywords && piece.seo_keywords.length > 0) {
          formattedText += `SEO Keywords: ${piece.seo_keywords.join(', ')}\n`;
        }
        if (piece.hashtags && piece.hashtags.length > 0) {
          formattedText += `Hashtags: ${piece.hashtags.join(' ')}\n`;
        }
        if (piece.visual_suggestions) {
          formattedText += `Visual Suggestions: ${piece.visual_suggestions}\n`;
        }
      });
    }

    if (content.success_metrics && content.success_metrics.length > 0) {
      formattedText += '\nSuccess Metrics:\n';
      content.success_metrics.forEach((metric: string) => {
        formattedText += `• ${metric}\n`;
      });
    }

    if (content.next_steps && content.next_steps.length > 0) {
      formattedText += '\nNext Steps:\n';
      content.next_steps.forEach((step: string) => {
        formattedText += `• ${step}\n`;
      });
    }

    return formattedText;
  }

  exportContent(content: any): void {
    const brandName = this.recommendation?.brand || 'brand';
    const category = this.recommendation?.category || 'content';
    
    const data = {
      brand: brandName,
      category: category,
      recommendation: this.recommendation,
      detailed_content: content,
      generated_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const fileName = `${brandName.toLowerCase().replace(/\s+/g, '-')}-${category.toLowerCase().replace(/\s+/g, '-')}-content-${Date.now()}.json`;
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  closePopup(): void {
    this.close.emit();
  }

  getPriorityColor(priority: string): string {
    switch (priority?.toLowerCase()) {
      case 'high': return 'warn';
      case 'medium': return 'accent';
      case 'low': return 'primary';
      default: return 'primary';
    }
  }

  // Manual method to force change detection
  forceChangeDetection(): void {
    // console.log('🔄 Forcing change detection...');
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  // Debug method to test variable updates
  debugVariables(): void {
    // console.log('🔍 Debug Variables:', {
    //   isLoading: this.isLoading,
    //   error: this.error,
    //   detailedContent: this.detailedContent,
    //   recommendation: this.recommendation
    // });

    // Force change detection
    this.forceChangeDetection();
  }

  // Test loading state changes
  testLoadingState(): void {
    // console.log('⚡ Testing loading state changes...');

    // Reset data first
    this.resetData();

    // Test loading state
    this.isLoading = true;
    // console.log('🔄 Set isLoading to true:', this.isLoading);
    this.cdr.detectChanges();

    // Simulate API delay
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = { test: 'Test content for debugging' };
      // console.log('🔄 Set isLoading to false and added test content:', this.isLoading, this.detailedContent);
      this.cdr.detectChanges();
    }, 2000);
  }

  // Simple loading test
  simpleLoadingTest(): void {
    // console.log('🧪 Simple loading test...');

    // Reset data first
    this.resetData();

    // Set loading
    setTimeout(() => {
      this.isLoading = true;
      // console.log('🔄 Set loading to true');
      this.cdr.detectChanges();
    }, 500);

    // Stop loading and add content
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = {
        content_overview: 'Test overview',
        content_pieces: [{ title: 'Test piece', content: 'Test content' }]
      };
      // console.log('🔄 Set loading to false and added content');
      this.cdr.detectChanges();
    }, 2000);
  }

  // Test BehaviorSubject approach
  testBehaviorSubject(): void {
    // console.log('🚀 Testing BehaviorSubject approach...');

    // Reset data first
    this.resetData();

    // Set loading
    setTimeout(() => {
      this.isLoading = true;
      // console.log('🔄 Set loading to true via BehaviorSubject');
    }, 500);

    // Stop loading and add content
    setTimeout(() => {
      this.isLoading = false;
      this.detailedContent = {
        content_overview: 'BehaviorSubject Test',
        content_pieces: [{ title: 'Test piece', content: 'Test content from BehaviorSubject' }]
      };
      // console.log('🔄 Set loading to false and added content via BehaviorSubject');
    }, 2000);
  }

  // Convert brand analysis response to content generation format
  private convertBrandAnalysisToContent(brandAnalysis: any, brandName: string, recommendation: any): any {
    const category = recommendation.category || 'General';
    const issue = recommendation.issue || 'General improvement needed';
    
    // Create content pieces based on the brand analysis
    const contentPieces = [];
    
    // Add a general content piece based on the category and issue
    contentPieces.push({
      type: `${category} Content Strategy`,
      title: `${brandName} ${category} Enhancement Strategy`,
      content: `Based on our analysis, ${brandName} should focus on improving their ${category} presence. The current issue "${issue}" can be addressed through strategic content creation and optimization.

Key areas for improvement:
- Enhance brand messaging and positioning
- Improve engagement and interaction
- Optimize content for better reach and impact
- Address specific performance gaps

Recommended approach:
1. Develop targeted content that addresses the specific needs
2. Implement consistent branding across all touchpoints
3. Create engaging, value-driven content
4. Monitor and optimize performance metrics`,
      target_audience: `${brandName} customers and prospects`,
      platform: category,
      seo_keywords: [brandName.toLowerCase(), category.toLowerCase(), 'content strategy', 'brand enhancement'],
      hashtags: [`#${brandName.replace(/\s+/g, '')}`, `#${category.replace(/\s+/g, '')}`, '#ContentStrategy', '#BrandEnhancement'],
      call_to_action: `Learn more about ${brandName}'s ${category} strategy`,
      visual_suggestions: `Professional images showcasing ${brandName}'s brand and ${category} presence`
    });

    // Add specific content piece based on the issue
    if (issue && issue !== 'General improvement needed') {
      contentPieces.push({
        type: 'Issue-Specific Content',
        title: `Addressing ${issue}`,
        content: `This content piece specifically addresses the identified issue: "${issue}"

Solution approach:
- Identify root causes of the current challenges
- Develop targeted content to address specific gaps
- Implement measurement and tracking systems
- Create feedback loops for continuous improvement

Expected outcomes:
- Improved performance in the identified area
- Better alignment with brand objectives
- Enhanced customer experience and engagement
- Measurable improvement in key metrics`,
        target_audience: `${brandName} stakeholders and customers`,
        platform: category,
        seo_keywords: [brandName.toLowerCase(), 'improvement', 'solution', category.toLowerCase()],
        hashtags: [`#${brandName.replace(/\s+/g, '')}Improvement`, '#Solution', `#${category.replace(/\s+/g, '')}Optimization`],
        call_to_action: `Discover how ${brandName} is addressing this challenge`,
        visual_suggestions: `Infographics showing before/after scenarios and improvement metrics`
      });
    }

    return {
      content_overview: `This content strategy is designed to enhance ${brandName}'s ${category} presence and address the specific issue: "${issue}". The approach focuses on creating targeted, engaging content that drives measurable improvements in brand performance.`,
      implementation_timeline: '2-4 weeks for initial content creation, with ongoing optimization and monitoring',
      content_pieces: contentPieces,
      success_metrics: [
        `Improve ${category} performance by 25-40%`,
        `Increase engagement and interaction rates`,
        `Enhance brand visibility and recognition`
      ],
      next_steps: [
        'Review and approve content strategy',
        'Begin content creation and development',
        'Implement tracking and measurement systems',
        'Monitor performance and optimize based on results'
      ]
    };
  }

  // Build content generation specific prompt
  private buildContentGenerationPrompt(brandName: string, recommendation: any): string {
    const category = recommendation.category || 'General';
    const issue = recommendation.issue || 'General improvement needed';
    const currentScore = recommendation.current_score || 0;

    return `CONTENT GENERATION REQUEST - NOT BRAND ANALYSIS

You are a content strategy expert specializing in brand content creation and optimization. 

IMPORTANT: This is a CONTENT GENERATION request, NOT a brand analysis request. Do NOT return brand analysis data like composite_score, competitor_analysis, or performance tables.

BRAND DETAILS:
- Brand Name: ${brandName}
- Category: ${category}
- Current Issue: ${issue}
- Current Score: ${currentScore}/100

CONTENT GENERATION REQUIREMENTS:
Generate detailed, actionable content that addresses the specific issue and improves the brand's performance in the ${category} category. The content should be:

1. Brand-specific and tailored to ${brandName}
2. Professional and engaging
3. Ready for immediate implementation
4. Optimized for the target platform/category
5. Include specific calls-to-action and engagement strategies

RESPONSE FORMAT:
Provide a comprehensive content strategy with the following structure:

{
  "success": true,
  "brand_name": "${brandName}",
  "category": "${category}",
  "detailed_content": {
    "content_overview": "Brief overview of the content strategy and its objectives",
    "implementation_timeline": "Timeline for content creation, review, and launch",
    "content_pieces": [
      {
        "type": "content type (e.g., landing page, social post, video script)",
        "title": "Compelling title for the content piece",
        "content": "Complete, ready-to-use content text with proper formatting",
        "target_audience": "Description of the target audience",
        "platform": "Specific platform or channel",
        "seo_keywords": ["keyword1", "keyword2", "keyword3"],
        "hashtags": ["#hashtag1", "#hashtag2", "#hashtag3"],
        "call_to_action": "Specific call-to-action text",
        "visual_suggestions": "Detailed suggestions for visual elements"
      }
    ],
    "success_metrics": [
      "Specific metric 1 with target",
      "Specific metric 2 with target"
    ],
    "next_steps": [
      "Actionable next step 1",
      "Actionable next step 2"
    ]
  },
  "generated_at": "ISO timestamp"
}

Focus on creating 3-5 high-quality content pieces that directly address the issue "${issue}" and will improve the brand's performance in the ${category} category. Make all content brand-specific to ${brandName} and avoid generic templates.

REMEMBER: This is CONTENT GENERATION, not brand analysis. Return detailed_content, not composite_score or competitor_analysis.`;
  }
}

